import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Avatar, Box, Button, Checkbox, CssBaseline, FormControlLabel, Grid, IconButton, Link, Paper, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { ChangeEvent } from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Copyright } from "../../../components/Copyright";
import { useBackendServices } from "../../../services/backend";
import type { SchoolSignUpParameters } from "../../../services/backend/types";
import { buildSignInPageUrl, buildVerifyEmailAddressPageUrl } from "../../../utils/BuildURLs";
import { ExceptionStatusCode, SCHOOL_BACKGROUND_IMAGE } from "../../../utils/Constants";
import { ToastError500 } from "../../../utils/UI/ToastError500";
import { borderRadiusStandard } from "../../../theme";

export const SignUpPage = () => {
  const { backendService } = useBackendServices();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [inputData, setInputData] = useState({
    schoolName: "",
    schoolAdminEmail: "",
    password: "",
    repeatPassword: "",
  });

  const { mutate: schoolSignUp, isLoading: isSigningUp } = useMutation<unknown, AxiosError, SchoolSignUpParameters>(
    ({ Name, Email, Password }: SchoolSignUpParameters) =>
      backendService.schoolSignUp({
        Name,
        Email,
        Password,
      }),
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    schoolSignUp(
      { Name: inputData.schoolName, Email: inputData.schoolAdminEmail, Password: inputData.password },
      {
        onSuccess: () => {
          const verifyEmailAddressPageUrl = buildVerifyEmailAddressPageUrl({
            emailAddress: inputData.schoolAdminEmail,
          });
          navigate(verifyEmailAddressPageUrl);
        },
        onError: (error: AxiosError) => {
          if (error.response!.status === ExceptionStatusCode.InternalServerException) {
            toast(<ToastError500 />);
          }
          if (error.response!.status === ExceptionStatusCode.ConflictException) {
            toast.error(t("authentication.sign_up_page.email_already_exists_error_message", { email: inputData.schoolAdminEmail }));
          }
          if (error.response!.status === ExceptionStatusCode.ValidationException) {
            toast.error(t("authentication.sign_up_page.invalid_parameters"));
          }
        },
      },
    );
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputData({
      ...inputData,
      [event.target.name]: event.target.value.trimStart().trimEnd(),
    });
  };

  const validatePassword = () => {
    if (inputData.password !== inputData.repeatPassword) {
      toast.error(t("common.passwords_not_match_message"));
      return false;
    }
    return true;
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: SCHOOL_BACKGROUND_IMAGE,
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundSize: "contain",
          backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {t("authentication.sign_up_page.school_sign_up")}
          </Typography>
          <Typography component="h1" variant="h5">
            {t("authentication.sign_up_page.create_free_account")}
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoComplete="school-name"
                  name="schoolName"
                  required
                  fullWidth
                  id="schoolName"
                  label={t("authentication.sign_up_page.school_name")}
                  autoFocus
                  onChange={handleInputChange}
                  disabled={isSigningUp}
                  sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="schoolAdminEmail"
                  label={t("authentication.sign_up_page.school_admin_email")}
                  name="schoolAdminEmail"
                  autoComplete="email"
                  onChange={handleInputChange}
                  disabled={isSigningUp}
                  sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label={t("authentication.sign_up_page.password")}
                  type={showNewPassword ? "text" : "password"}
                  id="password"
                  autoComplete="new-password"
                  onChange={handleInputChange}
                  disabled={isSigningUp}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        edge="end"
                        aria-label="show password"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        onMouseDown={() => setShowNewPassword(true)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    ),
                  }}
                  sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="repeatPassword"
                  label={t("authentication.sign_up_page.repeat_password")}
                  type={showRepeatPassword ? "text" : "password"}
                  id="repeatPassword"
                  autoComplete="new-password"
                  onChange={handleInputChange}
                  disabled={isSigningUp}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        edge="end"
                        aria-label="repeat new password"
                        onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                        onMouseDown={() => setShowRepeatPassword(true)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    ),
                  }}
                  sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Checkbox value="allowExtraEmails" color="primary" />} label={t("authentication.sign_up_page.email_updates")} />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={(event) => {
                event.currentTarget?.form?.current?.reportValidity();
                if (!validatePassword()) event.preventDefault();
              }}
              sx={{ mt: 3, mb: 2, borderRadius: borderRadiusStandard }}
            >
              {t("authentication.sign_up_page.sign_up")}
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link href={buildSignInPageUrl({ email: inputData.schoolAdminEmail })} variant="body2">
                  {t("authentication.sign_up_page.sign_in")}
                </Link>
              </Grid>
            </Grid>
            <Copyright sx={{ mt: 5 }} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
